import { MegaMenuColumn, MegaMenuSegment } from '@layouts/MegaMenu/MegaMenu.types';
import React, { ReactNode, createContext, useContext, useEffect, useState, useRef } from 'react';

interface ActiveItems {
  [key: string]: string;
}

type MegaMenuData = MegaMenuSegment | MegaMenuColumn;

interface MegaMenuStateProviderProps {
  selectedSection: string;
  megaMenuData: MegaMenuSegment;
  children: ReactNode;
}

interface MegaMenuStateContext {
  activeItems: ActiveItems;
  data: MegaMenuData;
  addActiveItem: (changedLevel: number, item: string) => void;
  resetActiveItems: () => void;
  removeActiveItem: (level: number) => void;
  resetData: () => void;
  setData: (data: MegaMenuData) => void;
}

const MegaMenuState = createContext<MegaMenuStateContext>(null);

export const MegaMenuStateProvider = ({
  selectedSection,
  megaMenuData,
  children,
}: MegaMenuStateProviderProps) => {
  // obj where obj key is the level and the value is the active item
  const [activeItems, setActiveItems] = useState<ActiveItems>({});
  const initialData = useRef<MegaMenuData>(megaMenuData);
  const [data, setData] = useState<MegaMenuData>(megaMenuData);

  const addActiveItem = (changedLevel: number, item: string) => {
    // remove all active items that come after changed active item
    const newActiveItems = { ...activeItems };

    Object.keys(newActiveItems).forEach((level) => {
      if (parseInt(level) > changedLevel) {
        delete newActiveItems[level];
      }
    });

    setActiveItems({ ...newActiveItems, [changedLevel.toString()]: item });
  };

  useEffect(() => {
    resetActiveItems();
  }, [selectedSection, megaMenuData]);

  useEffect(() => {
    setData(megaMenuData);
    initialData.current = megaMenuData;
  }, [megaMenuData]);

  const resetActiveItems = () => {
    setActiveItems({});
  };

  const removeActiveItem = (level: number) => {
    delete activeItems[level.toString()];
  };

  const resetData = () => {
    setData(initialData.current);
    resetActiveItems();
  };

  return (
    <MegaMenuState.Provider
      value={{
        activeItems,
        data,
        addActiveItem,
        resetActiveItems,
        removeActiveItem,
        resetData,
        setData: (newData) => {
          setData(newData);
        },
      }}
    >
      {children}
    </MegaMenuState.Provider>
  );
};

export const useMegaMenuState = () => {
  const context = useContext(MegaMenuState);

  return context;
};
